import React from 'react';
import PropTypes from 'prop-types';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { isEmpty, filter, isEqual } from 'lodash';
import uuid from 'uuid/v1';

import { Tooltip, Card, CardMedia, Button, Typography, Box, IconButton } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { renderTable } from '../../MaterialMUIComponent';

import Loading from '../../Loading';

import TemplatePointCloudItemEdit from './TemplatePointCloudItemEdit';

import { ELEMENT_TYPES } from '../../../../api/AnnotationTemplates/AnnotationTemplates';
import * as templateOps from '../../../../api/AnnotationTemplates/annotation-template-operations';

import OpenModalFromUrl from '../../OpenModalFromUrl';
import TablePagination from '../../TablePagination';

import TelemetryConnections from '../../../../api/TelemetryConnections/TelemetryConnections';

const TemplatePointCloudItem = ({
  elementItem,
  updateAnnotationFields,
  serverConnection,
  loading,
  isAdvancedMode,
  ...rest
}) => {
  if (loading) return <Loading />;
  const multiPointCloudSchema = templateOps.getElementMultiplePointCloud(elementItem);
  const isMultiPoint = isEqual(
    templateOps.getElementType(elementItem),
    ELEMENT_TYPES.MULTIPLEPOINTCLOUD
  );

  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 7;
  const totalCount = multiPointCloudSchema.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const [multiPointCloudToShow, setMultiPointCloudToShow] = React.useState(
    filter(multiPointCloudSchema, (element, index) => index < itemsPerPage)
  );
  const startPointCloud =
    !isMultiPoint && !isEmpty(templateOps.getElementConnections(elementItem))
      ? templateOps.getElementConnections(elementItem)
      : {};
  const [pointCloudSelected, setPointCloudSelected] = React.useState(startPointCloud);
  const [openFullScreen, setOpenFullScreen] = React.useState(false);

  const changePage = pageNumber => {
    const indexFilter = itemsPerPage * pageNumber;
    const prevIndexFilter = itemsPerPage * (pageNumber - 1);
    const newDatasToShow = filter(
      multiPointCloudSchema,
      (element, index) => index >= prevIndexFilter && index < indexFilter
    );
    setMultiPointCloudToShow(newDatasToShow);
    setCurrentPage(pageNumber);
  };

  const handleViewMultiPointCloud = multiPointCloud => {
    if (!isEmpty(pointCloudSelected.source)) {
      if (isEqual(multiPointCloud.source.name, pointCloudSelected.source.name))
        setPointCloudSelected({});
    } else setPointCloudSelected(multiPointCloud);
  };

  const elementsTable = [
    {
      row: 1,
      column: 0,
      rowJustifyContent: templateOps.getElemenPosition(elementItem),
      isShowRow: true,
      showElement: true,
      elements: [
        multiPointCloudToShow.map(item => {
          return (
            <Tooltip key={uuid()} title={item.source.name}>
              <Card key={uuid()} style={{ height: '120px', width: '120px', marginRight: '2%' }}>
                <Button
                  key={uuid()}
                  style={{ padding: 0 }}
                  onClick={() => handleViewMultiPointCloud(item)}
                >
                  <CardMedia
                    key={uuid()}
                    component="img"
                    style={{ height: '100%' }}
                    image={item.source.imgLink}
                  />
                </Button>
              </Card>
            </Tooltip>
          );
        })
      ]
    },
    {
      row: 2,
      column: 0,
      rowJustifyContent: 'flex-end',
      isShowRow: true,
      showElement: !isEmpty(multiPointCloudSchema),
      elements: [
        totalPages > 1 && (
          <Typography
            marginTop="1%"
            marginRight="1%"
          >{`${currentPage} - ${totalPages}`}</Typography>
        ),
        <TablePagination
          key={uuid()}
          currentPage={currentPage}
          isPaginationOn
          itemsPerPage={itemsPerPage}
          stretchView
          totalCount={totalCount}
          changePage={changePage}
        />
      ]
    }
  ];

  return (
    <>
      {!isAdvancedMode && (
        <>
          {isMultiPoint && <>{renderTable(elementsTable)}</>}
          {!isEmpty(pointCloudSelected) && (
            <>
              <Box
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '50vh'
                }}
              >
                <Box
                  style={{ zIndex: 'tooltip', position: 'absolute', width: '100%', height: '100%' }}
                >
                  <iframe
                    title="WebPage"
                    src={pointCloudSelected.source.link}
                    style={{ width: '100%', height: '100%' }}
                  />
                </Box>
                <Box
                  style={{
                    zIndex: 'modal',
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    top: '90%'
                  }}
                >
                  <IconButton
                    style={{
                      color: 'white',
                      size: 'lg'
                    }}
                    onClick={() => setOpenFullScreen(true)}
                  >
                    <FullscreenIcon />
                  </IconButton>
                </Box>
              </Box>
              <OpenModalFromUrl
                isFullScreen
                isImage={false}
                isLoading
                openModal={openFullScreen}
                setOpenModal={setOpenFullScreen}
                url={pointCloudSelected.source.link}
                title={pointCloudSelected.source.name}
              />
            </>
          )}
        </>
      )}
      {isAdvancedMode && (
        <>
          <TemplatePointCloudItemEdit
            elementItem={elementItem}
            updateAnnotationFields={updateAnnotationFields}
            serverConnection={serverConnection}
            {...rest}
          />
        </>
      )}
    </>
  );
};
TemplatePointCloudItem.defaultProps = {};

TemplatePointCloudItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func.isRequired,
  serverConnection: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  isAdvancedMode: PropTypes.bool.isRequired
};

export default withTracker(() => {
  const connectionsSub = Meteor.isClient && Meteor.subscribe('telemetryConnections');
  return {
    loading: !connectionsSub.ready(),
    serverConnection: TelemetryConnections.find({ serverType: { $in: ['IGP', 'IGPS'] } }).fetch()
  };
})(TemplatePointCloudItem);
